import { debounce } from "lodash";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../../api/fastapi";
import { useHookCache } from "../../lib/hookCache";
import { useLiveDocumentProcessingEnabled } from "../useLiveDocumentProcessingEnabled";
import { AuditSession } from "../../models/audit/auditSession";
import {
  ControlSessionEnvelope,
  TestPhase,
} from "../../models/audit/controlSession";
import {
  SessionKind,
  sessionEnvelopeCacheKey,
} from "./useControlSessionEnvelope";
const DOC_ASSIGNMENT_DEBOUNCE_MS = 750;

interface CreateSourceRowParams {
  auditSession: AuditSession;
  attributes: string[];
  data: Record<string, string | number | null>;
}

interface CreateSamplesParams {
  auditSession: AuditSession;
  attributes: string[];
  data: Array<Record<string, string | number | null>>;
}

interface UseAuditMutationsArgs {
  /** Current control session envelope; required for control-scoped mutations. */
  controlSession: ControlSessionEnvelope | null;
  /** UI-state side effects that stay in `auditContext`. */
  notifyDocumentUploadStarted: () => void;
  /**
   * Which entity heads the session: session-scoped mutations (rename,
   * submit-for-review, reopen) hit `/work-steps/work-step-sessions` instead of
   * `/controls/v2/control-sessions` for `"work_step"`. Control-only mutations
   * (test phase, FY cycle) are no-ops for work step sessions.
   */
  sessionKind?: SessionKind;
}

/**
 * Sibling hook to `auditContext` that owns every mutation handler previously
 * defined inline in `AuditProvider`. Each mutation does an optimistic write
 * (where the legacy code had one) via `useHookCache().setEntry`, fires the
 * API call, then invalidates the affected cache keys.
 *
 * Signatures intentionally match the legacy `useAudit()` shape so the
 * slimmed `AuditProvider` can spread these into its context value without
 * forcing 19+ consumer call-sites to change in this PR.
 *
 * User-facing handler count is **12**. `updateDocumentAssignments` is the
 * 750ms debounced internal helper for `updateDocumentCategory` and is
 * inlined below — D1's 13-row inventory becomes 12 once that helper is
 * folded into its caller.
 */
export function useAuditMutations({
  controlSession,
  notifyDocumentUploadStarted,
  sessionKind = "control",
}: UseAuditMutationsArgs) {
  const { setEntry, invalidate } = useHookCache();
  // Root for session-scoped mutation paths (PATCH / submit-for-review).
  const sessionApiRoot =
    sessionKind === "work_step"
      ? "/work-steps/work-step-sessions"
      : "/controls/v2/control-sessions";
  // Root for the singular session-scoped document-upload endpoints below
  // (upload-documents-to-procedures / upload-guidance-documents-to-procedures).
  const sessionScopedRoot =
    sessionKind === "work_step"
      ? "/work-steps/work-step-session"
      : "/controls/v2/control-session";
  // UI copy: work step sessions surface as "Test" (never "Control session").
  const sessionNoun = sessionKind === "work_step" ? "Test" : "Control session";
  // When the live document-processing view is enabled it supersedes the
  // per-upload "uploaded successfully…" confirmation, so suppress the redundant
  // success toast to stop it stacking in front of the live view.
  const suppressUploadToast = useLiveDocumentProcessingEnabled();

  // --- Source rows -----------------------------------------------------

  const uploadSampleListToAuditSession = useCallback(
    async (auditSession: AuditSession, sampleList: File[]) => {
      if (!auditSession?.id) {
        console.error("Audit session not found");
        toast.error("Audit session not found");
        return;
      }
      try {
        const formData = new FormData();
        sampleList.forEach((file) => {
          formData.append("files", file);
        });

        await fetchFastApi({
          method: "POST",
          path: `/matching/session/${auditSession.id}/upload-source-rows`,
          body: formData,
        });
        toast.success(
          "Sample list now being processed - this may take a few seconds",
        );

        await invalidate(["procedure-session", auditSession.id, "source-rows"]);
        if (controlSession?.id) {
          await invalidate(["control-session", controlSession.id, "status"]);
        }
      } catch (error) {
        console.error("Error uploading sample list:", error);
        toast.error("Error uploading sample list");
        throw error;
      }
    },
    [controlSession, invalidate],
  );

  /**
   * Cache refresh shared by the two in-app sample-creation mutations. It
   * invalidates the same key set as the CSV/Excel and Optro flows
   * (`AuditUploadSampleList`, `AuditUploadAdditionalSamples`), though those
   * fire theirs unawaited, so a caller of this one waits for the refetch.
   *
   * Both keys are deliberately the PARENT prefix, because `invalidate` is a
   * forward prefix match — a sub-key never reaches its parent. Creating the
   * first sample manually used to invalidate `[…, "source-rows"]` and
   * `[…, "status"]` only, which left two parents stale until a page reload
   * (SOX-108388):
   *
   *   - the control-session envelope holds `sourceRowsCount`, which the
   *     Sample-Specific and General Evidence buttons gate on, so both stayed
   *     greyed out even though the new sample was already on screen;
   *   - the heavy procedure-session hydration holds `procedure.attributes`,
   *     which the first sample initialises server-side, so the next "Add a
   *     sample" dialog reported "No attributes defined".
   *
   * The control session comes from the closure rather than a parameter: two
   * positional ids of the same type can be transposed silently, and a
   * transposed pair matches no cache entry at all, which would restore the
   * exact bug above with no error anywhere to trace it back to.
   */
  const invalidateAfterSampleCreate = useCallback(
    async (procedureSessionId: string) => {
      const pending = [invalidate(["procedure-session", procedureSessionId])];
      if (controlSession?.id) {
        pending.push(invalidate(["control-session", controlSession.id]));
      }
      await Promise.all(pending);
    },
    [controlSession, invalidate],
  );

  const createSourceRow = useCallback(
    async ({ auditSession, attributes, data }: CreateSourceRowParams) => {
      if (!controlSession?.id) {
        console.error("Audit session not found");
        toast.error("Audit session not found");
        return;
      }
      try {
        const body: Record<string, string | number | object> = {
          attributes,
          data,
        };

        await fetchFastApi({
          method: "POST",
          path: `/matching/match-session/${auditSession.id}/source-row/create`,
          body,
        });

        toast.success("Sample created. Processing may take a few seconds.");

        await invalidateAfterSampleCreate(auditSession.id);
      } catch (error) {
        console.error("Error creating source row:", error);
        toast.error("Error creating sample");
        throw error;
      }
    },
    [controlSession, invalidateAfterSampleCreate],
  );

  const createSamples = useCallback(
    async ({ auditSession, attributes, data }: CreateSamplesParams) => {
      if (!controlSession?.id) {
        console.error("Audit session not found");
        toast.error("Audit session not found");
        return;
      }
      try {
        const body = { attributes, data };

        await fetchFastApi({
          method: "POST",
          path: `/matching/match-session/${auditSession.id}/create-samples`,
          body,
        });

        toast.success(
          `${data.length} sample${data.length > 1 ? "s" : ""} created. Processing may take a few seconds.`,
        );

        await invalidateAfterSampleCreate(auditSession.id);
      } catch (error) {
        console.error("Error creating samples:", error);
        toast.error("Error creating samples");
        throw error;
      }
    },
    [controlSession, invalidateAfterSampleCreate],
  );

  // --- Documents -------------------------------------------------------

  const uploadDocumentsToAuditSession = useCallback(
    async (
      auditSession: AuditSession,
      documents: File[],
      sourceRowId?: string,
      uploadIds?: string[],
    ) => {
      if (!controlSession?.id) {
        console.error("Audit session not found");
        toast.error("Control session not found");
        return;
      }
      try {
        const formData = new FormData();

        if (uploadIds && uploadIds.length > 0) {
          formData.append("upload_ids", uploadIds.join(","));
        } else {
          documents.forEach((file) => {
            formData.append("files", file);
          });
        }

        formData.append("should_match_documents", "true");
        formData.append("source_row_id", sourceRowId ?? "");

        await fetchFastApi({
          method: "POST",
          path: `/matching/match-session/${auditSession.id}/upload-documents`,
          body: formData,
        });

        // Re-open the live processing stream (it closes once everything is
        // terminal) so the status view picks up this fresh upload.
        notifyDocumentUploadStarted();

        if (!suppressUploadToast) {
          toast.success(
            `${documents.length} document${
              documents.length > 1 ? "s" : ""
            } uploaded successfully${
              sourceRowId
                ? ` to Sample ${
                    auditSession.sourceRows.find(
                      (sourceRow) => sourceRow.id === sourceRowId,
                    )?.sampleNumber
                  }`
                : ""
            }. This may take a few seconds to process.`,
            { duration: 10000 },
          );
        }

        await invalidate(["procedure-session", auditSession.id, "documents"]);
        if (sourceRowId) {
          await invalidate([
            "procedure-session",
            auditSession.id,
            "source-rows",
          ]);
        }
        await invalidate(["control-session", controlSession.id, "status"]);
      } catch (error) {
        console.error("Error uploading documents:", error);
        toast.error("Error uploading documents");
      }
    },
    [
      controlSession,
      invalidate,
      suppressUploadToast,
      notifyDocumentUploadStarted,
    ],
  );

  const uploadDocumentsToControlSessionProcedures = useCallback(
    async (
      documents: File[],
      procedureSessionIds: string[],
      _sourceRowId?: string,
      shouldMatchDocuments?: boolean,
      uploadIds?: string[],
    ) => {
      if (!controlSession?.id) {
        console.error("Control session not found");
        toast.error("Control session not found");
        return;
      }
      try {
        const formData = new FormData();

        if (uploadIds && uploadIds.length > 0) {
          formData.append("upload_ids", uploadIds.join(","));
        } else {
          documents.forEach((file) => {
            formData.append("files", file);
          });
        }

        formData.append("procedure_session_ids", procedureSessionIds.join(","));
        if (shouldMatchDocuments !== undefined) {
          formData.append(
            "should_match_documents",
            shouldMatchDocuments.toString(),
          );
        }

        await fetchFastApi({
          method: "POST",
          path: `${sessionScopedRoot}/${controlSession.id}/upload-documents-to-procedures`,
          body: formData,
        });

        if (!suppressUploadToast) {
          toast.success(
            `${documents.length} document${
              documents.length > 1 ? "s" : ""
            } uploaded successfully to ${procedureSessionIds.length} procedure${
              procedureSessionIds.length > 1 ? "s" : ""
            }. This may take a few seconds to process.`,
            { duration: 10000 },
          );
        }

        notifyDocumentUploadStarted();

        await invalidate(["control-session", controlSession.id, "status"]);
        for (const psId of procedureSessionIds) {
          await invalidate(["procedure-session", psId, "documents"]);
        }
      } catch (error) {
        console.error("Error uploading documents:", error);
        toast.error("Error uploading documents");
      }
    },
    [
      controlSession,
      invalidate,
      notifyDocumentUploadStarted,
      suppressUploadToast,
      sessionScopedRoot,
    ],
  );

  const uploadGuidanceDocumentsToControlSessionProcedures = useCallback(
    async (
      documents: File[],
      procedureSessionIds: string[],
      uploadIds?: string[],
    ) => {
      if (!controlSession?.id) {
        console.error("Control session not found");
        toast.error("Control session not found");
        return;
      }
      try {
        const formData = new FormData();

        if (uploadIds && uploadIds.length > 0) {
          formData.append("upload_ids", uploadIds.join(","));
        } else {
          documents.forEach((file) => {
            formData.append("files", file);
          });
        }

        formData.append("procedure_session_ids", procedureSessionIds.join(","));

        await fetchFastApi({
          method: "POST",
          path: `${sessionScopedRoot}/${controlSession.id}/upload-guidance-documents-to-procedures`,
          body: formData,
        });

        if (!suppressUploadToast) {
          toast.success(
            `${documents.length} guidance document${
              documents.length > 1 ? "s" : ""
            } uploaded successfully to ${procedureSessionIds.length} procedure${
              procedureSessionIds.length > 1 ? "s" : ""
            }. This may take a few seconds to process.`,
            { duration: 10000 },
          );
        }

        notifyDocumentUploadStarted();

        await invalidate(["control-session", controlSession.id, "status"]);
        for (const psId of procedureSessionIds) {
          await invalidate(["procedure-session", psId, "documents"]);
        }
      } catch (error) {
        console.error("Error uploading guidance documents:", error);
        toast.error("Error uploading guidance documents");
      }
    },
    [
      controlSession,
      invalidate,
      notifyDocumentUploadStarted,
      suppressUploadToast,
      sessionScopedRoot,
    ],
  );

  // --- Document category (optimistic + debounced + rollback) ----------

  const updateDocumentAssignments = useMemo(
    () =>
      debounce(
        async (
          documentId: string,
          categoryIds: string[],
          auditSessionId: string,
        ) => {
          try {
            await fetchFastApi({
              method: "POST",
              path: `/matching/match-session/${auditSessionId}/document/${documentId}/update-document-assignments`,
              body: { document_assignments: categoryIds },
            });

            toast.success("Document assignments updated successfully");
            // Confirm the optimistic write by re-fetching the slice.
            await invalidate([
              "procedure-session",
              auditSessionId,
              "documents",
            ]);
          } catch (error) {
            console.error("Error updating document assignments:", error);
            toast.error("Error updating document assignments");
            // Roll back optimistic update by invalidating the whole procedure
            // session prefix (matches documents + source-rows + envelope).
            await invalidate(["procedure-session", auditSessionId]);
          }
        },
        DOC_ASSIGNMENT_DEBOUNCE_MS,
      ),
    [invalidate],
  );

  const updateDocumentCategory = useCallback(
    (auditSession: AuditSession, documentId: string, categoryIds: string[]) => {
      if (!auditSession) return;

      // Optimistic update on the documents slice: rewrite the affected
      // document's `fetchableDocument.documentAssignments`.
      setEntry<{
        documents: AuditSession["documents"];
      }>(["procedure-session", auditSession.id, "documents"], (prev) => {
        return {
          ...prev,
          documents: prev.documents.map((doc) => {
            const docUniqueId = doc.documentId ?? doc.id;
            if (docUniqueId !== documentId) return doc;
            return {
              ...doc,
              fetchableDocument: doc.fetchableDocument
                ? {
                    ...doc.fetchableDocument,
                    documentAssignments: categoryIds.map((categoryId) => {
                      const requiredDoc =
                        auditSession.procedure.requiredDocuments.find(
                          (rd) => rd.id === categoryId,
                        );
                      return {
                        id: categoryId,
                        shortName: requiredDoc?.shortName || categoryId,
                        description: requiredDoc?.description || null,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                      };
                    }),
                  }
                : null,
            };
          }),
        };
      });

      // Cache shape changed in support-tab-lazy-load: per-row hydration is
      // keyed by ["procedure-session", id, "source-rows", sourceRowId]. We
      // do not know which sourceRowId held this document client-side, so
      // invalidate the prefix and let the per-row + slim-list hooks refetch
      // on next read.
      void invalidate(["procedure-session", auditSession.id, "source-rows"]);

      void updateDocumentAssignments(documentId, categoryIds, auditSession.id);
    },
    [setEntry, invalidate, updateDocumentAssignments],
  );

  // --- Control session envelope mutations ------------------------------

  const updateControlSessionName = useCallback(
    async (name: string) => {
      if (!controlSession) return;
      const csId = controlSession.id;
      const envelopeKey = sessionEnvelopeCacheKey(csId, sessionKind);
      const previousName = controlSession.name;

      setEntry<ControlSessionEnvelope>(envelopeKey, (prev) => ({
        ...prev,
        name,
      }));

      try {
        await fetchFastApi({
          method: "PATCH",
          path: `${sessionApiRoot}/${csId}`,
          body: { name: name.trim() },
        });
        toast.success(`${sessionNoun} name updated`);
      } catch (error) {
        // Rollback on error: restore previous name, then invalidate to
        // ensure consistency with server state.
        setEntry<ControlSessionEnvelope>(envelopeKey, (prev) => ({
          ...prev,
          name: previousName,
        }));
        await invalidate(["control-session", csId]);
        console.error(
          `Failed to update ${sessionNoun.toLowerCase()} name:`,
          error,
        );
        toast.error(`Failed to update ${sessionNoun.toLowerCase()} name`);
        throw error;
      }
    },
    [
      controlSession,
      setEntry,
      invalidate,
      sessionApiRoot,
      sessionKind,
      sessionNoun,
    ],
  );

  const updateControlSessionPhase = useCallback(
    async (testPhase: TestPhase) => {
      // Control-only field: work step sessions have no test phase (the edit
      // dialog hides the field; this guard keeps a stray call harmless).
      if (!controlSession || sessionKind === "work_step") return;
      const csId = controlSession.id;
      const previousPhase = controlSession.testPhase;

      setEntry<ControlSessionEnvelope>(["control-session", csId], (prev) => ({
        ...prev,
        testPhase,
      }));

      try {
        await fetchFastApi({
          method: "PATCH",
          path: `/controls/v2/control-sessions/${csId}`,
          body: { test_phase: testPhase },
        });
      } catch (error) {
        setEntry<ControlSessionEnvelope>(["control-session", csId], (prev) => ({
          ...prev,
          testPhase: previousPhase,
        }));
        await invalidate(["control-session", csId]);
        console.error("Failed to update test phase:", error);
        toast.error("Failed to update test phase");
        throw error;
      }
    },
    [controlSession, setEntry, invalidate, sessionKind],
  );

  const updateControlSessionFyCycle = useCallback(
    async (fyCycleId: string | null) => {
      // Control-only field: work step sessions have no FY cycle.
      if (!controlSession || sessionKind === "work_step") return;
      const csId = controlSession.id;
      try {
        await fetchFastApi({
          method: "PATCH",
          path: `/controls/v2/control-sessions/${csId}`,
          body: { fy_cycle_id: fyCycleId },
        });
        await invalidate(["control-session", csId]);
      } catch (error) {
        console.error("Failed to update FY cycle:", error);
        toast.error("Failed to update FY cycle");
        throw error;
      }
    },
    [controlSession, invalidate, sessionKind],
  );

  const submitForReview = useCallback(async () => {
    if (!controlSession) return;
    const csId = controlSession.id;
    const envelopeKey = sessionEnvelopeCacheKey(csId, sessionKind);
    const previousStatus = controlSession.status;

    setEntry<ControlSessionEnvelope>(envelopeKey, (prev) => ({
      ...prev,
      status: "in_review",
    }));

    try {
      const response = await fetchFastApi({
        method: "POST",
        path: `${sessionApiRoot}/${csId}/submit-for-review`,
      });
      const newStatus: ControlSessionEnvelope["status"] = response.status;
      setEntry<ControlSessionEnvelope>(envelopeKey, (prev) => ({
        ...prev,
        status: newStatus,
      }));

      if (newStatus === "complete") {
        toast.success(`${sessionNoun} completed`);
      } else {
        toast.success(`${sessionNoun} submitted for review`);
      }
    } catch (error) {
      setEntry<ControlSessionEnvelope>(envelopeKey, (prev) => ({
        ...prev,
        status: previousStatus,
      }));
      await invalidate(["control-session", csId]);
      console.error("Failed to submit for review:", error);
      toast.error("Failed to submit for review");
      throw error;
    }
  }, [
    controlSession,
    setEntry,
    invalidate,
    sessionApiRoot,
    sessionKind,
    sessionNoun,
  ]);

  const reopenSession = useCallback(async () => {
    if (!controlSession) return;
    const csId = controlSession.id;
    const envelopeKey = sessionEnvelopeCacheKey(csId, sessionKind);
    const previousStatus = controlSession.status;

    setEntry<ControlSessionEnvelope>(envelopeKey, (prev) => ({
      ...prev,
      status: "in_progress",
    }));

    try {
      // Reopen is a plain status PATCH for both kinds — there is no dedicated
      // reopen endpoint (mirrors the controls behavior).
      await fetchFastApi({
        method: "PATCH",
        path: `${sessionApiRoot}/${csId}`,
        body: { status: "in_progress" },
      });
      toast.success(`${sessionNoun} reopened`);
    } catch (error) {
      setEntry<ControlSessionEnvelope>(envelopeKey, (prev) => ({
        ...prev,
        status: previousStatus,
      }));
      await invalidate(["control-session", csId]);
      console.error("Failed to reopen session:", error);
      toast.error("Failed to reopen session");
      throw error;
    }
  }, [
    controlSession,
    setEntry,
    invalidate,
    sessionApiRoot,
    sessionKind,
    sessionNoun,
  ]);

  return {
    uploadSampleListToAuditSession,
    uploadDocumentsToAuditSession,
    uploadDocumentsToControlSessionProcedures,
    uploadGuidanceDocumentsToControlSessionProcedures,
    createSourceRow,
    createSamples,
    updateDocumentCategory,
    updateControlSessionName,
    updateControlSessionPhase,
    updateControlSessionFyCycle,
    submitForReview,
    reopenSession,
  };
}
